import { authService } from 'auth/services';

import { getHostname, uuidv4 } from 'features/order-tracker/utils';

import {
  AemHeadlessPropsService,
  AmendAppointmentSiteContactService,
  DeliveryDetailsService,
  GroupOrderService,
  OrderSummaryService,
  TrackingEventsService,
  amendPreAppointmentService,
  recaptchaService,
} from '../services';
import { AEM_HEADLESS_REQUEST_TYPE, AMEND_APPOINTMENT_REQUEST_TYPE } from './constants';
import { getAmendAppointmentRequestBody } from './helpers/amendAppointment';
import { ResponseValidator } from './responseValidator';

const REACT_APP_APIGEE_HOST = import.meta.env.VITE_APIGEE_HOST;
const REACT_APP_APIGEE_BASE_PATH = import.meta.env.VITE_APIGEE_BASE_PATH;
const REACT_APP_APIGEE_BASE_AUTH_PATH = import.meta.env.VITE_APIGEE_BASE_AUTH_PATH;
const REACT_APP_APIGEE_B2C_BASE_PATH = import.meta.env.VITE_APIGEE_B2C_BASE_PATH;
const REACT_APP_OT_APIGW_CLIENT_ID = import.meta.env.VITE_OT_APIGW_CLIENT_ID;
const REACT_APP_AEM_HEADLESS_BASE = import.meta.env.VITE_AEM_HEADLESS_BASE;
const REACT_APP_SAF_CLIENT_ID = import.meta.env.VITE_SAF_CLIENT_ID;

const getRequestHeaders = async (type?: string): Promise<HeadersInit> => {
  const headers = { 'Content-Type': 'application/json; charset=utf-8' };

  if (type === AEM_HEADLESS_REQUEST_TYPE) {
    return headers;
  }

  const recaptchaToken = await recaptchaService.getRecaptchaToken();

  return {
    ...headers,
    'APIGW-Client-Id': REACT_APP_OT_APIGW_CLIENT_ID,
    'APIGW-Tracking-Header': uuidv4(),
    ...(type !== AMEND_APPOINTMENT_REQUEST_TYPE ? { 'Recaptcha-Response-Token': recaptchaToken } : {}),
  } as HeadersInit;
};

const getRequestAuthHeaders = async (type?: string, method?: string): Promise<HeadersInit> => {
  const requestHeaders = await getRequestHeaders(type);
  const isAuthenticated = authService.isAuthenticated();

  const authorization = {};
  if (isAuthenticated) {
    const [tokenType, accessToken] = await authService.getAccessToken();
    Object.assign(authorization, {
      Authorization: `${tokenType} ${accessToken}`,
    });
  }

  const headers = {
    ...requestHeaders,
    ...authorization,
    ...(type !== AMEND_APPOINTMENT_REQUEST_TYPE ? { isAuthenticated: isAuthenticated.toString() } : {}),
    'APIGW-Client-Id': isAuthenticated ? REACT_APP_SAF_CLIENT_ID : REACT_APP_OT_APIGW_CLIENT_ID,
  } as HeadersInit;

  if (method === 'POST') {
    return {
      ...headers,
      'Content-Type': 'application/json',
    };
  }

  return headers;
};

const hostname = getHostname();
const baseUrlAPIGEE = `${REACT_APP_APIGEE_HOST}${REACT_APP_APIGEE_BASE_PATH}`;
const baseUrlAuthAPIGEE = `${REACT_APP_APIGEE_HOST}${REACT_APP_APIGEE_BASE_AUTH_PATH}`;
const baseUrlB2cAPIGEE = `${REACT_APP_APIGEE_HOST}${REACT_APP_APIGEE_B2C_BASE_PATH}`;
const baseUrlAemHeadless = `${hostname}${REACT_APP_AEM_HEADLESS_BASE}`;

export const getResource = async ({
  url = '',
  options = { method: 'GET' },
  baseUrl,
  headers,
}: api.RequestDataType): Promise<any> => {
  const requestHeaders = headers || (await getRequestHeaders());
  const response = await fetch(`${baseUrl}${url}`, {
    ...options,
    headers: requestHeaders,
  });

  return ResponseValidator.validateResponse(response);
};

export const getGroupOrder = async (orderId) => {
  const url = `/group-orders/${orderId}`;
  const response: Response<dto.GroupOrderSummaryDTO> = await getResource({ url, baseUrl: baseUrlAPIGEE });

  return GroupOrderService.transformResponse(response);
};

export const getOrderSummary = async (orderId, postcode) => {
  const url = `/users-journey/orders/${orderId}?postCode=${postcode}`;
  const baseUrl = authService.isAuthenticated() ? baseUrlB2cAPIGEE : baseUrlAPIGEE;

  const headers = await getRequestAuthHeaders();
  const response = await getResource({ url, baseUrl, headers });
  return OrderSummaryService.transformResponse(response);
};

export const getAuthAmendableAttribute = async (orderKey: string, itemId: string): Promise<void | never> => {
  const url = `/orders/${orderKey}/${itemId}/amendable?attributes=APPOINTMENT,SITECONTACT`;
  const headers = await getRequestAuthHeaders(AMEND_APPOINTMENT_REQUEST_TYPE);
  const response = await getResource({ url, baseUrl: baseUrlAuthAPIGEE, headers });

  const { APPOINTMENT, SITECONTACT } = response.result;

  if (!APPOINTMENT || !SITECONTACT) {
    throw new Error('warning');
  }
};

export const getOrderAppointments = async (
  orderKey: string,
  itemId: string,
  date: string,
  isInitRequest?: boolean,
): Promise<common.PreAppointmentData> => {
  const url = isInitRequest
    ? `/orders/${orderKey}/${itemId}/pre-appointments?date=${date}`
    : `/order-appointments/${orderKey}/${itemId}?date=${date}`;
  const headers = await getRequestAuthHeaders(AMEND_APPOINTMENT_REQUEST_TYPE);
  const response = await getResource({ url, baseUrl: baseUrlAuthAPIGEE, headers });

  return amendPreAppointmentService.normalizeAppointments(response.result?.Appointments || [], date, isInitRequest);
};

export const getSiteContacts = async (
  orderKey: string,
  itemId: string,
  postCode: string,
): Promise<common.Contact[]> => {
  const url = `/order-site-contacts/${orderKey}/${itemId}?postCode=${postCode}`;
  const headers = await getRequestHeaders(AMEND_APPOINTMENT_REQUEST_TYPE);
  const response = await getResource({ url, baseUrl: baseUrlAPIGEE, headers });

  return AmendAppointmentSiteContactService.transformResponse(response.result);
};

export const postAmendAppointment = async (
  amendAppointmentData: api.AmendAppointmentData,
  isDateChanged: boolean,
  isSiteContactChanged: boolean,
): Promise<void> => {
  const body = getAmendAppointmentRequestBody(amendAppointmentData, isDateChanged, isSiteContactChanged);
  const headers = await getRequestAuthHeaders(AMEND_APPOINTMENT_REQUEST_TYPE, 'POST');
  const options = { method: 'POST', body: JSON.stringify(body) };

  const response = await getResource({ url: '/orders', baseUrl: baseUrlAuthAPIGEE, options, headers });

  if (response?.result?.startsWith?.('OTError:')) {
    throw new Error(response.result);
  }
};

export const getDeliveryDetails = async (orderId: string, postcode: string): Promise<dto.OrderDeliveryDTO[]> => {
  const url = `/users-journey/orders/${orderId}/delivery-details?postCode=${postcode}`;
  const baseUrl = authService.isAuthenticated() ? baseUrlB2cAPIGEE : baseUrlAPIGEE;
  const headers = await getRequestAuthHeaders();
  const response: Response<dto.OrderDeliveryDTO[]> = await getResource({ url, baseUrl, headers });

  return DeliveryDetailsService.transformResponse(response);
};

export const getTrackingEvents = async (trackingData): Promise<common.DeliveryTrackingInfo | ''> => {
  const { itemReference, trackingReference, carrier, orderId } = trackingData;
  const trackUnit = `${trackingReference}:${carrier}`;
  const url = `/orders/${orderId}/items/${itemReference}/tracking-events?trackUnit=${trackUnit}`;
  const headers = await getRequestAuthHeaders();
  const baseUrl = authService.isAuthenticated() ? baseUrlB2cAPIGEE : baseUrlAPIGEE;

  try {
    const response: dto.TrackingEventsDTO = await getResource({
      url,
      baseUrl,
      headers,
    });
    return TrackingEventsService.transformResponse(response);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('Tracking Events Resource:', message);
  }

  return '';
};

export const getAEMSummaryProps = async () => {
  const headers = await getRequestHeaders(AEM_HEADLESS_REQUEST_TYPE);
  const response = await getResource({
    url: '',
    baseUrl: baseUrlAemHeadless,
    headers,
  });
  return AemHeadlessPropsService.transformResponse(response);
};
